import { posts as blogPosts } from 'virtual:blog-index';

export const MARKETING_ORIGIN = (
  import.meta.env.VITE_MARKETING_URL || 'https://poplearn.chat'
).replace(/\/+$/, '');

export { blogPosts };

export const DEFAULT_SOCIAL_IMAGE = `${MARKETING_ORIGIN}/logo512.png`;

export type StructuredData = Record<string, unknown>;

export interface SeoConfig {
  title: string;
  description: string;
  canonical?: string;
  robots: string;
  image: string;
  type: 'website' | 'article';
  structuredData: StructuredData[];
  includeInSitemap?: boolean;
  /** Set on blog pages so readers can discover the RSS feed. */
  feed?: string;
}

interface PublicRouteDefinition {
  path: string;
  title: string;
  description: string;
  schemaType?: 'WebPage' | 'ContactPage' | 'Blog';
  includeInSitemap?: boolean;
  type?: 'website' | 'article';
  /** Overrides the default WebPage + BreadcrumbList pair when set. */
  structuredData?: StructuredData[];
  lastmod?: string;
}

export const BLOG_AUTHOR = 'The Poplearn Team';

const productDescription =
  'Poplearn is a Slack-native LMS that turns existing training material into bite-sized modules, delivers them by direct message, and tracks completion automatically.';

const publicRouteDefinitions: PublicRouteDefinition[] = [
  {
    path: '/',
    title: 'Poplearn — bite-sized training your team actually finishes',
    description:
      'Build bite-sized employee training, deliver it in Slack, and track completion automatically with Poplearn, the Slack-native LMS.',
    includeInSitemap: true,
  },
  {
    path: '/pricing',
    title: 'Poplearn Pricing — Slack-native employee training',
    description:
      'Explore Poplearn pricing for Slack-native employee training, including a free plan and flexible monthly or annual options.',
    includeInSitemap: true,
  },
  {
    path: '/support',
    title: 'Poplearn Support',
    description:
      'Get help with Poplearn, the Slack-native learning management system for bite-sized employee training.',
    schemaType: 'ContactPage',
    includeInSitemap: true,
  },
  {
    path: '/privacy',
    title: 'Poplearn Privacy Policy',
    description:
      'Read the Poplearn privacy policy and learn how Urple, LLC handles personal information.',
    schemaType: 'WebPage',
  },
  {
    path: '/terms',
    title: 'Poplearn Terms of Service',
    description:
      'Read the terms that govern use of the Poplearn website and Slack-native learning platform.',
    schemaType: 'WebPage',
  },
  {
    path: '/cookies',
    title: 'Poplearn Cookie Policy',
    description:
      'Learn how Poplearn uses cookies and related technologies across its website and services.',
    schemaType: 'WebPage',
  },
  {
    path: '/acceptable-use',
    title: 'Poplearn Acceptable Use Policy',
    description:
      'Read the acceptable use requirements for the Poplearn website and Slack-native learning platform.',
    schemaType: 'WebPage',
  },
];

const normalizePath = (pathname: string): string => {
  const path = pathname.split(/[?#]/, 1)[0] || '/';
  if (path === '/') {
    return path;
  }
  return path.replace(/\/+$/, '');
};

const canonicalForPath = (path: string): string =>
  `${MARKETING_ORIGIN}${path === '/' ? '/' : path}`;

const organizationSchema: StructuredData = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Poplearn',
  legalName: 'Urple, LLC',
  url: `${MARKETING_ORIGIN}/`,
  logo: `${MARKETING_ORIGIN}/logo512.png`,
  email: 'hello@urple.co',
};

const softwareApplicationSchema: StructuredData = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Poplearn',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web, Slack',
  url: `${MARKETING_ORIGIN}/`,
  description: productDescription,
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
    category: 'Free plan',
  },
};

const breadcrumbSchema = (
  title: string,
  path: string,
): StructuredData => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Poplearn',
      item: `${MARKETING_ORIGIN}/`,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: title.replace(/^Poplearn\s*/, ''),
      item: canonicalForPath(path),
    },
  ],
});

export const buildArticleStructuredData = ({
  title,
  description,
  path,
  datePublished,
  dateModified = datePublished,
  authorName,
  image = DEFAULT_SOCIAL_IMAGE,
}: {
  title: string;
  description: string;
  path: string;
  datePublished: string;
  dateModified?: string;
  authorName: string;
  image?: string;
}): StructuredData => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: title,
  description,
  datePublished,
  dateModified,
  mainEntityOfPage: canonicalForPath(normalizePath(path)),
  image,
  author: {
    '@type': 'Person',
    name: authorName,
  },
  publisher: {
    '@type': 'Organization',
    name: 'Poplearn',
    logo: {
      '@type': 'ImageObject',
      url: `${MARKETING_ORIGIN}/logo512.png`,
    },
  },
});

const BLOG_INDEX_PATH = '/blog';

const blogIndexDefinition: PublicRouteDefinition = {
  path: BLOG_INDEX_PATH,
  title: 'Poplearn Blog — notes on training people actually finish',
  description:
    'Practical writing on employee onboarding, compliance training, and microlearning that gets finished — from the team behind the Slack-native LMS Poplearn.',
  includeInSitemap: true,
  structuredData: [
    {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: 'Poplearn Blog',
      url: canonicalForPath(BLOG_INDEX_PATH),
      description:
        'Practical writing on onboarding, compliance training, and microlearning.',
      publisher: {
        '@type': 'Organization',
        name: 'Poplearn',
        url: `${MARKETING_ORIGIN}/`,
      },
    },
    breadcrumbSchema('Blog', BLOG_INDEX_PATH),
  ],
  lastmod: blogPosts[0]?.updated,
};

// Comparison pages are evergreen reference material, so they deliberately skip
// Article schema (and the visible byline/date that comes with it). Essays are
// dated writing and get the full Article treatment.
const blogPostDefinitions: PublicRouteDefinition[] = blogPosts.map((post) => ({
  path: post.path,
  title: post.title,
  description: post.description,
  includeInSitemap: true,
  type: post.template === 'essay' ? 'article' : 'website',
  lastmod: post.updated,
  structuredData:
    post.template === 'essay'
      ? [
          buildArticleStructuredData({
            title: post.title,
            description: post.description,
            path: post.path,
            datePublished: post.date,
            dateModified: post.updated,
            authorName: BLOG_AUTHOR,
          }),
          breadcrumbSchema(post.title, post.path),
        ]
      : [
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: post.title,
            description: post.description,
            url: canonicalForPath(post.path),
            isPartOf: {
              '@type': 'WebSite',
              name: 'Poplearn',
              url: `${MARKETING_ORIGIN}/`,
            },
          },
          breadcrumbSchema(post.title, post.path),
        ],
}));

const allRouteDefinitions: PublicRouteDefinition[] = [
  ...publicRouteDefinitions,
  blogIndexDefinition,
  ...blogPostDefinitions,
];

export const PUBLIC_ROUTES = allRouteDefinitions.map(({ path }) => path);

export const SITEMAP_ROUTES = allRouteDefinitions
  .filter(({ includeInSitemap }) => includeInSitemap)
  .map(({ path }) => path);

/** Sitemap rows with the `lastmod` the plain-JS prerender script needs. */
export const SITEMAP_ENTRIES = allRouteDefinitions
  .filter(({ includeInSitemap }) => includeInSitemap)
  .map(({ path, lastmod }) => ({ path, lastmod }));

export const isPublicRoute = (pathname: string): boolean =>
  PUBLIC_ROUTES.includes(normalizePath(pathname));

export const getSeoConfig = (pathname: string): SeoConfig => {
  const path = normalizePath(pathname);
  const route = allRouteDefinitions.find((candidate) => candidate.path === path);

  if (!route) {
    return {
      title: 'Page not found — Poplearn',
      description: 'The requested Poplearn page could not be found.',
      robots: 'noindex, nofollow',
      image: DEFAULT_SOCIAL_IMAGE,
      type: 'website',
      structuredData: [],
    };
  }

  const structuredData: StructuredData[] =
    route.structuredData ??
    (path === '/'
      ? [organizationSchema, softwareApplicationSchema]
      : [
          {
            '@context': 'https://schema.org',
            '@type': route.schemaType || 'WebPage',
            name: route.title,
            description: route.description,
            url: canonicalForPath(path),
            isPartOf: {
              '@type': 'WebSite',
              name: 'Poplearn',
              url: `${MARKETING_ORIGIN}/`,
            },
          },
          breadcrumbSchema(route.title, path),
        ]);

  return {
    title: route.title,
    description: route.description,
    canonical: canonicalForPath(path),
    robots: 'index, follow',
    image: DEFAULT_SOCIAL_IMAGE,
    type: route.type ?? 'website',
    structuredData,
    includeInSitemap: route.includeInSitemap,
    feed: path.startsWith('/blog') ? `${MARKETING_ORIGIN}/feed.xml` : undefined,
  };
};
